import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Every file Gyro creates is saved in Documents\Gyro\<chat name> as soon as it appears (owner, 2026-10-22, after a gyro-svc
 * deploy took files a person had not downloaded yet, twice in one day). DESKTOP APP ONLY: the shell announces it
 * (`window.__gyroNativeFiles.autoSave`, bluebeamStudio.ts nativeFiles) and the native agent does the saving
 * (SpotlightOverlay.AutoSave.cs, GeneratedFileSaver.cs); the web panel and the Office pane are unchanged.
 *  - A gyro-svc file (an answer's Generated files): when the answer finishes, the widget names it by its two ids
 *    (`gyro:auto-save`, kind "artifact"); the agent downloads it under the person's own custody, as their Download does.
 *  - A file made on this PC (an Export output, a code block's Download as Markdown): the widget hands over its bytes
 *    (kind "file", base64, at most 8 MB).
 * The agent writes each one once (by its key), never over a file, never a type Gyro does not offer, and answers
 * (`gyro:auto-save-result`). Under the file list one line says where the copy is, with "Open folder", or why there is none.
 */
import { useSyncExternalStore } from 'react';
/** Where the copies are, as the person reads it. */
export const AUTO_SAVE_FOLDER = 'Documents\\Gyro';
/** The one area the agent takes: StarScribe page exports beside no chat, Documents\Gyro\StarScribe\<page title>. */
export const STARSCRIBE_AREA = 'StarScribe';
/** The largest file this page hands over (localDocuments.ts's document limit). */
export const MAX_PAGE_FILE_BYTES = 8 * 1024 * 1024;
/**
 * gyro-svc's BLOCKED_OUTPUT_EXTENSIONS (services/gyro-svc/src/codeExecution.ts): the kinds of file Gyro never offers, so
 * never copied either (the agent refuses them too; desktopAutoSave.test.ts compares the two lists).
 */
export const NEVER_SAVED_EXTENSIONS = new Set([
    'exe', 'com', 'scr', 'pif', 'cpl', 'dll', 'sys', 'msi', 'msp', 'msix', 'msixbundle', 'appx', 'appxbundle', 'appinstaller',
    'bat', 'cmd', 'ps1', 'psm1', 'psd1', 'vbs', 'vbe', 'js', 'jse', 'wsf', 'wsh', 'hta', 'sct', 'jar', 'py', 'pyw',
    'lnk', 'url', 'scf', 'reg', 'inf', 'chm', 'gadget', 'application', 'settingcontent-ms',
    'iso', 'img', 'vhd', 'vhdx',
    'xlsm', 'xltm', 'xlam', 'xlsb', 'docm', 'dotm', 'pptm', 'potm', 'ppsm', 'ppam', 'sldm',
    'html', 'htm', 'xhtml', 'xht', 'shtml', 'mht', 'mhtml', 'svg', 'svgz', 'xll', 'iqy', 'slk', 'one', 'msc', 'jnlp',
    'library-ms', 'search-ms', 'website', 'ade', 'adp', 'mde', 'accde', 'appref-ms', 'diagcab', 'wsc',
]);
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const KEY = /^(a:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|l:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|c:[0-9a-f]{40})$/;
const clean = (value, max) => typeof value === 'string' && value.trim().length > 0 && value.length <= max && !/[\u0000-\u001f\u007f]/.test(value);
/** True for a name whose type Gyro never offers, judged on its last extension (as gyro-svc judges it). */
export function neverSaved(name) {
    const trimmed = name.trim().replace(/[. ]+$/, '');
    const dot = trimmed.lastIndexOf('.');
    return dot >= 0 && NEVER_SAVED_EXTENSIONS.has(trimmed.slice(dot + 1).toLowerCase());
}
/** A file's key, saved at most once per PC: `a:<task>:<file>` for a gyro-svc file, `l:<id>` for one made on this PC; null when its ids are not UUIDs. */
export function autoSaveKey(artifact) {
    if (artifact.source === 'desktop')
        return UUID.test(artifact.id) ? `l:${artifact.id.toLowerCase()}` : null;
    return UUID.test(artifact.taskId) && UUID.test(artifact.id) ? `a:${artifact.taskId.toLowerCase()}:${artifact.id.toLowerCase()}` : null;
}
const RESULT_STATUSES = new Set(['saved', 'failed', 'skipped', 'open_failed']);
/** The agent's answer (`gyro:auto-save-result`), validated field by field. */
export function parseAutoSaveResult(detail) {
    if (!detail || typeof detail !== 'object')
        return null;
    const { key, status, folder, fileName, message, downloadable } = detail;
    if (typeof key !== 'string' || !KEY.test(key) || typeof status !== 'string' || !RESULT_STATUSES.has(status))
        return null;
    if ((folder !== undefined && !clean(folder, 400)) || (fileName !== undefined && !clean(fileName, 260)) || (message !== undefined && !clean(message, 300)))
        return null;
    if (downloadable !== undefined && typeof downloadable !== 'boolean')
        return null;
    return {
        key, status: status,
        ...(folder ? { folder } : {}), ...(fileName ? { fileName } : {}), ...(message ? { message } : {}),
        ...(typeof downloadable === 'boolean' ? { downloadable } : {}),
    };
}
function listNames(names) {
    if (names.length === 1)
        return names[0];
    if (names.length === 2)
        return `${names[0]} and ${names[1]}`;
    return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}
/**
 * The lines for `entries`: "Saved to Documents\Gyro\<chat name>" once a copy is saved (the first saved copy's folder, which
 * Open folder shows); "Couldn't save a copy to Documents\Gyro: <reason>. Download it from here." when copies failed (naming
 * them when only some of the list failed, and without "Download it from here" when gyro-svc no longer has the file).
 * Nothing while copies are still being made, and nothing for a type Gyro never keeps.
 */
export function autoSaveLines(entries, state, openFailure) {
    const lines = {};
    const tracked = entries.filter(entry => { const current = state(entry.key); return current !== undefined && current.status !== 'skipped'; });
    for (const entry of tracked) {
        const current = state(entry.key);
        if (current?.status === 'saved') {
            lines.saved = { key: entry.key, folder: current.folder };
            break;
        }
    }
    const failed = tracked.flatMap(entry => { const current = state(entry.key); return current?.status === 'failed' ? [{ entry, current }] : []; });
    if (failed.length) {
        const reason = failed[0].current.message.trim().replace(/[.\s]+$/, '');
        const which = failed.length === tracked.length ? '' : ` of ${listNames(failed.map(item => item.entry.name))}`;
        const download = failed.every(item => item.current.downloadable) ? ` Download ${failed.length === 1 ? 'it' : 'them'} from here.` : '';
        lines.failed = `Couldn't save a copy${which} to ${AUTO_SAVE_FOLDER}: ${reason}.${download}`;
    }
    if (lines.saved && openFailure) {
        const sentence = openFailure(lines.saved.key);
        if (sentence)
            lines.openFailed = sentence;
    }
    return lines;
}
/** A blob's bytes as standard base64 (what the agent's strict parser reads). */
export async function blobBase64(blob) {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let binary = '';
    for (let at = 0; at < bytes.length; at += 0x8000)
        binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000));
    return btoa(binary);
}
async function sha256Hex(text) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}
/**
 * The page's side of the copies: what it asked for, what the agent answered, and the line each list shows. One per widget
 * (GyroAssistant creates it only where the desktop shell announces the copies); every chat binds it with its own name.
 */
export class DesktopAutoSave {
    host;
    states = new Map();
    openFailures = new Map();
    listeners = new Set();
    revision = 0;
    onResult = (event) => this.receive(event.detail);
    constructor(host) {
        this.host = host;
        host.addEventListener('gyro:auto-save-result', this.onResult);
    }
    dispose() { this.host.removeEventListener('gyro:auto-save-result', this.onResult); }
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    };
    getRevision = () => this.revision;
    state(key) { return this.states.get(key); }
    openFailure(key) { return this.openFailures.get(key); }
    /**
     * The binding the components under one chat use: its saves go in `chat`'s folder; `chatId` scopes code-block keys.
     * `besideChat: false` (a chat with nothing in it yet, or a StarScribe page's own window) sends a page's exports to
     * Documents\Gyro\StarScribe\<page title> instead (docs/STRATEGY-starscribe.md section 10).
     */
    bind(chat, chatId, options = {}) {
        const besideChat = options.besideChat !== false;
        return {
            saveLocal: (artifact, blob) => { void this.saveLocal(chat, artifact, blob); },
            saveCode: (fileName, text) => this.saveText(chat, chatId, fileName, text),
            Note: this.Note,
            saveExport: (artifact, pageTitle) => this.saveExport(besideChat ? { chat } : { pageTitle }, artifact),
            ExportNote: this.ExportNote,
        };
    }
    /**
     * A StarScribe page's export (a gyro-svc file): ask for its copy, in the chat's folder or, beside no chat, in
     * Documents\Gyro\StarScribe\<page title> (the agent's one area). The copy's key, or null when none is asked for (the page
     * then downloads the file). Every export is a new file, so a key already known is answered from what is known.
     */
    saveExport(target, artifact) {
        if (artifact.source === 'desktop')
            return null;
        const key = autoSaveKey(artifact);
        if (!key || neverSaved(artifact.name) || !clean(artifact.name, 500))
            return null;
        if (this.states.has(key) && this.states.get(key)?.status !== 'failed')
            return key;
        const folder = 'chat' in target ? { chat: target.chat } : { chat: target.pageTitle.slice(0, 1000), area: STARSCRIBE_AREA };
        this.set(key, { status: 'saving' });
        // `export` marks a page export, the only copy the agent's Open in Word opens.
        this.send({ kind: 'artifact', key, ...folder, name: artifact.name, taskId: artifact.taskId.toLowerCase(), id: artifact.id.toLowerCase(), export: 'starscribe' });
        return key;
    }
    /** "Open in Word" under a StarScribe export's copy: the agent opens the Word document it saved. Only the key travels. */
    openFile(key) {
        if (this.openFailures.delete(key))
            this.changed();
        this.host.dispatchEvent(new CustomEvent('gyro:auto-save-open', { detail: { key, file: true } }));
    }
    /** An answer finished with gyro-svc files: ask for a copy of each (the agent downloads it), once per file. */
    saveArtifacts(chat, artifacts) {
        for (const artifact of artifacts ?? []) {
            if (artifact.source === 'desktop')
                continue;
            const key = autoSaveKey(artifact);
            if (!key || this.states.has(key))
                continue;
            if (neverSaved(artifact.name) || !clean(artifact.name, 500)) {
                this.set(key, { status: 'skipped' });
                continue;
            }
            this.set(key, { status: 'saving' });
            this.send({ kind: 'artifact', key, chat, name: artifact.name, taskId: artifact.taskId.toLowerCase(), id: artifact.id.toLowerCase() });
        }
    }
    /** A file made on this PC appeared (an Export output): hand its bytes over for a copy, once per file. */
    async saveLocal(chat, artifact, blob) {
        const key = autoSaveKey(artifact);
        if (!key || this.states.has(key))
            return;
        await this.saveBytes(key, chat, artifact.name, blob);
    }
    /** A code block was downloaded as Markdown: keep a copy of that same file, once per chat, name and text. */
    async saveText(chat, chatId, fileName, text) {
        let key;
        try {
            key = `c:${(await sha256Hex(`${chatId}\n${fileName}\n${text}`)).slice(0, 40)}`;
        }
        catch {
            return null;
        }
        const current = this.states.get(key);
        // A copy already made or on its way is not made again; a failed one is tried again by the new click.
        if (current && current.status !== 'failed')
            return key;
        this.states.delete(key);
        await this.saveBytes(key, chat, fileName, new Blob([text], { type: 'text/markdown' }));
        return key;
    }
    /** "Open folder": Explorer at the copy's folder with the copy selected. Only the key travels; the agent knows the path. */
    open(key) {
        if (this.openFailures.delete(key))
            this.changed();
        this.host.dispatchEvent(new CustomEvent('gyro:auto-save-open', { detail: { key } }));
    }
    /** The agent's answer. */
    receive(detail) {
        const result = parseAutoSaveResult(detail);
        if (!result)
            return;
        if (result.status === 'open_failed') {
            this.openFailures.set(result.key, result.message ?? 'Windows could not open the folder. Look in your Documents folder, under Gyro.');
            this.changed();
            return;
        }
        if (result.status === 'saved')
            this.set(result.key, { status: 'saved', folder: result.folder ?? AUTO_SAVE_FOLDER, fileName: result.fileName ?? '' });
        else if (result.status === 'failed')
            this.set(result.key, { status: 'failed', message: result.message ?? 'the copy could not be saved', downloadable: result.downloadable !== false });
        else
            this.set(result.key, { status: 'skipped' });
    }
    /** The line under a list or a downloaded code block (the binding's Note): a stable component per widget. */
    Note = (props) => _jsx(AutoSaveNote, { store: this, ...props });
    /** The line under a StarScribe page for its export's copy (the binding's ExportNote): a stable component per widget. */
    ExportNote = (props) => _jsx(ExportCopyNote, { store: this, ...props });
    async saveBytes(key, chat, name, blob) {
        if (neverSaved(name) || !clean(name, 500)) {
            this.set(key, { status: 'skipped' });
            return;
        }
        if (!blob.size || blob.size > MAX_PAGE_FILE_BYTES) {
            this.set(key, { status: 'failed', message: blob.size ? 'the file is larger than 8 MB' : 'the file is empty', downloadable: true });
            return;
        }
        this.set(key, { status: 'saving' });
        let data;
        try {
            data = await blobBase64(blob);
        }
        catch {
            this.set(key, { status: 'failed', message: 'the file could not be read', downloadable: true });
            return;
        }
        this.send({ kind: 'file', key, chat, name, data });
    }
    send(detail) {
        this.host.dispatchEvent(new CustomEvent('gyro:auto-save', { detail }));
    }
    set(key, state) {
        this.states.set(key, state);
        this.changed();
    }
    changed() {
        this.revision++;
        for (const listener of [...this.listeners])
            listener();
    }
}
/** The lines under a file list or a downloaded code block, following the agent's answers as they arrive. */
export function AutoSaveNote({ store, artifacts, entry }) {
    useSyncExternalStore(store.subscribe, store.getRevision, store.getRevision);
    const entries = entry ? [entry] : (artifacts ?? []).flatMap(artifact => { const key = autoSaveKey(artifact); return key ? [{ key, name: artifact.name }] : []; });
    const lines = autoSaveLines(entries, key => store.state(key), key => store.openFailure(key));
    const saved = lines.saved;
    if (!saved && !lines.failed)
        return null;
    return _jsxs(_Fragment, { children: [saved && _jsxs("p", { className: "gyro-file-preview-note", role: "status", children: ["Saved to ", saved.folder, " ", _jsx("button", { type: "button", className: "gyro-link", onClick: () => store.open(saved.key), children: "Open folder" })] }), lines.openFailed && _jsx("p", { className: "gyro-file-error", role: "status", children: lines.openFailed }), lines.failed && _jsx("p", { className: "gyro-file-error", role: "status", children: lines.failed })] });
}
/** True for a Word document, the one type "Open in Word" opens (the agent checks it again). */
export function isWordDocument(name) {
    return /\.docx$/i.test(name.trim());
}
/**
 * The line under a StarScribe page for its export's copy (docs/STRATEGY-starscribe.md section 10: "the same 'Saved to …'
 * line"): "Saving to Documents\Gyro…" until the agent answers; "Saved to <folder>" with Open folder, and Open in Word for a
 * Word document; or why no copy was kept, with Download when gyro-svc still has the file.
 */
export function ExportCopyNote({ store, copyKey, fileName, onDownload }) {
    useSyncExternalStore(store.subscribe, store.getRevision, store.getRevision);
    const current = store.state(copyKey);
    const openFailed = store.openFailure(copyKey);
    if (!current || current.status === 'saving')
        return _jsx("p", { className: "gyro-file-preview-note", role: "status", children: `Saving to ${AUTO_SAVE_FOLDER}…` });
    if (current.status === 'saved') {
        return _jsxs(_Fragment, { children: [_jsxs("p", { className: "gyro-file-preview-note", role: "status", children: ["Saved to ", current.folder, " ", _jsx("button", { type: "button", className: "gyro-link", onClick: () => store.open(copyKey), children: "Open folder" }), isWordDocument(current.fileName || fileName) && _jsxs(_Fragment, { children: [" ", _jsx("button", { type: "button", className: "gyro-link", onClick: () => store.openFile(copyKey), children: "Open in Word" })] })] }), openFailed && _jsx("p", { className: "gyro-file-error", role: "status", children: openFailed })] });
    }
    const download = _jsxs(_Fragment, { children: [" ", _jsx("button", { type: "button", className: "gyro-link", onClick: onDownload, children: "Download" })] });
    if (current.status === 'failed') {
        const reason = current.message.trim().replace(/[.\s]+$/, '');
        return _jsxs("p", { className: "gyro-file-error", role: "status", children: [`Couldn't save ${fileName} to ${AUTO_SAVE_FOLDER}: ${reason}.`, current.downloadable && download] });
    }
    return _jsxs("p", { className: "gyro-file-error", role: "status", children: [`${fileName} is not kept in ${AUTO_SAVE_FOLDER}.`, download] });
}
/** The full entry's part: the page's side of the copies, for a desktop shell that announces them. */
export function createAutoSave(host) {
    return new DesktopAutoSave(host);
}
//# sourceMappingURL=desktopAutoSave.js.map